<script setup lang="ts">
import {
  IonIcon,
  IonPage,
  IonContent,
  IonAlert,
  IonItem,
  IonLabel,
  IonLoading,
} from "@ionic/vue";

import { settings, trash, arrowBack } from "ionicons/icons";
import { deleteEvent } from "@/api/events";
import { handleError } from "@/views";
import router from "@/router";
import { reactive, watch } from "vue";
import { useToasters } from "@/composition/toasters";
import { useUserStore } from "@/stores/user";
import { useWindowWidth } from "@/composition/window";

const { isMobile } = useWindowWidth();

const toasters = useToasters();
const userStore = useUserStore();

interface IManagebleEvent {
  id: number;
  name: string;
  user_can_exclude: boolean;
}

const state = reactive({
  loading: true,
  alertEvent: false,
  alertEventManager: false,
  currentEventEdition: 0,
  events: [] as IManagebleEvent[],
});

const openDialogDeleteEvent = (edit_type: string, id: number) => {
  if (edit_type === "event") {
    state.currentEventEdition = id;
    state.alertEvent = true;
  } else {
    state.alertEventManager = true;
  }
};

const editEvent = (id: number) => {
  router.push(`/event/${id}/settings`);
};

watch(
  () => userStore.links.owns.ids,
  (event_ids) => {
    if (event_ids) {
      userStore.related.events.forEach((event) => {
        state.events.push({
          id: event.id,
          user_can_exclude: event_ids.includes(event.id),
          name: event.name,
        });
      });
    }
    state.loading = false;
  }
);

const deleteButtons = [
  {
    text: "Cancel",
    role: "cancel",
    cssClass: "alert-button-cancel",
    handler: () => {
      state.alertEvent = false;
      state.currentEventEdition = 0;
    },
  },
  {
    text: " OK ",
    role: "confirm",
    cssClass: "alert-button-confirm",
    handler: () => {
      state.alertEvent = false;
      deleteEventById(state.currentEventEdition);
    },
  },
];

const deleteEventById = (event_id: number) => {
  state.loading = true;
  deleteEvent(event_id)
    .then(() => {
      state.loading = false;
      router.push("/home");
      toasters.addSuccess("Event deleted successfully");
      userStore.fetchMe();
    })
    .catch((err) => {
      state.loading = false;
      if (err.response.data && err.response.data[0]) {
        toasters.addError(err.response.data[0]);
      } else {
        handleError(err);
      }
    });
};
</script>

<template>
  <ion-page>
    <ion-content v-if="isMobile">
      <div class="container" v-if="state.events.length">
        <h1>Events</h1>
        <ion-item v-for="(event, i) in state.events" :key="`event-${event.id}`">
          <span
            ><b>#{{ i + 1 }}</b
            >&nbsp;</span
          >
          <ion-label>{{ event.name }}</ion-label>
          <ion-icon
            :icon="settings"
            size="small"
            slot="end"
            @click="editEvent(event.id)"
          ></ion-icon>
          <ion-icon
            :icon="trash"
            size="small"
            slot="end"
            color="danger"
            @click="openDialogDeleteEvent('event', event.id)"
            v-if="event.user_can_exclude"
          ></ion-icon>
          <ion-alert
            :isOpen="state.alertEvent"
            :buttons="deleteButtons"
            header="Are you sure?"
            message="This will permanently delete your event."
          ></ion-alert>
        </ion-item>
      </div>
      <ion-loading
        :is-open="state.loading"
        message="Please wait..."
      ></ion-loading>
    </ion-content>
    <div class="colored-bg full-height" v-else>
      <div
        class="container"
        style="min-height: 300px"
        v-if="state.events.length"
      >
        <div class="flex-desktop back-title-container">
          <ion-fab-button
            v-if="!isMobile"
            class="fab-manage-events"
            color="primary"
            @click="$router.go(-1)"
          >
            <ion-icon :icon="arrowBack"></ion-icon>
          </ion-fab-button>
          <h1>Events</h1>
        </div>
        <ion-item v-for="(event, i) in state.events" :key="`event-${event.id}`">
          <span
            ><b>#{{ i + 1 }}</b
            >&nbsp;</span
          >
          <ion-label>{{ event.name }}</ion-label>
          <ion-icon
            :icon="settings"
            size="small"
            slot="end"
            @click="editEvent(event.id)"
          ></ion-icon>
          <ion-icon
            :icon="trash"
            size="small"
            slot="end"
            color="danger"
            @click="openDialogDeleteEvent('event', event.id)"
          ></ion-icon>
          <ion-alert
            :isOpen="state.alertEvent"
            :buttons="deleteButtons"
            header="Are you sure?"
            message="This will permanently delete your event."
          ></ion-alert>
        </ion-item>
      </div>
    </div>
  </ion-page>
</template>

<style scoped lang="scss">
.event_mngr_email {
  font-size: 12px !important;
}
.mngr_icon {
  margin-right: 10px;
}

.back-title-container {
  align-items: center;
  position: relative;
  right: 100px;
}

.fab-manage-events {
  margin-right: 42px;
}
</style>
